/**
 * Data Client — RPC client for the dashboard backend.
 *
 * Every query is routed to /api/* via apiClient.js. The legacy in-process
 * Web Worker (which served mock data during the migration) has been
 * retired now that every method is HTTP-backed; calling an unknown
 * method throws rather than silently spawning a worker.
 *
 * Provides a Promise-based query() function and React hooks for
 * declarative data fetching with stale-while-revalidate semantics.
 */

import { useCallback, useEffect, useMemo, useReducer, useRef, useState, useSyncExternalStore } from "react";
import { useSearchParams } from "react-router-dom";
import { httpQuery, isHttpMethod } from "./apiClient.js";
import { useMyPermissions } from "./permissions.js";
import { queryPermitted } from "./queryGate.js";
import {
  adapterStepsNeedingHydration,
  notifyScopeHydration,
  onScopeHydration,
  scopePathAwaitingHydration,
} from "./dimAdapters.js";
import { onGenerationPinChange, getGenerationRevision } from "./generationPin.js";
import { getAuthContextEpoch, onAuthContextChange } from "./authTokenStore.js";
import { DEFAULT_RANGE } from "./dateRangePresets.js";

// ── Current range hook ──────────────────────────────────────────
//
// Pulls the active date-range preset id (or custom range string) out of
// the URL so any component can fold it into its query params without
// prop-drilling. App.jsx is the writer (DateRangePicker → setSearchParams);
// every reader uses this hook so they always pick up the latest value.
export function useCurrentRange() {
  const [params] = useSearchParams();
  return params.get("range") || DEFAULT_RANGE;
}

// ── Scope-step hydration ────────────────────────────────────────
//
// Adapter-backed dims whose attribution is derived (Linear ticket →
// endpoints, GitHub PR → endpoints + window, etc.) own an async
// `hydrateScopeStep(step, range)` that populates their internal cache
// off-band. Once cached, the adapter's `rewriteScopeStep` returns the
// resolved scope steps synchronously and `toApiScope` picks them up.
//
// This hook walks the current scope path, finds adapter steps that
// declare hydrateScopeStep, fires hydration once per (adapter, id,
// range), and subscribes to the adapter's notify channel so the
// React tree re-renders when resolution completes. Mount it once at
// the App level — every scope-consuming query downstream then sees
// the freshly-rewritten scope.
//
// In-flight set prevents the same (adapter, id, range) from being
// fired twice. Adapters are responsible for their own cache hit
// detection (i.e. hydrateScopeStep MAY be called more than once per
// (id, range) across page lifetimes; the adapter no-ops if already
// resolved).
const _hydrationInflight = new Set();

export function useScopeHydration(scopePath, range) {
  // Subscribe once at mount; a counter forces a re-render after any
  // hydration completion so downstream useQuery hooks reading scope
  // through their params will see the new resolution.
  const [, bump] = useReducer((x) => x + 1, 0);
  useEffect(() => {
    return onScopeHydration(() => bump());
  }, []);

  // Fire hydrations for every adapter step in scope that hasn't been
  // hit yet for this (id, range). The adapter's notify() inside its
  // own hydrateScopeStep is what eventually triggers the bump above.
  useEffect(() => {
    const needs = adapterStepsNeedingHydration(scopePath);
    for (const { adapter, step } of needs) {
      const id = (step.ids && step.ids[0]) ?? step.id;
      const key = `${adapter.id}|${String(id)}|${JSON.stringify(range ?? null)}`;
      if (_hydrationInflight.has(key)) continue;
      _hydrationInflight.add(key);
      Promise.resolve(adapter.hydrateScopeStep({ step, range }))
        .then(() => notifyScopeHydration())
        .catch((err) => {
          console.warn(`[hydration] ${key} failed:`, err);
        })
        .finally(() => {
          // Keep the key in the inflight set so we don't refire on
          // the same render cycle. Adapter caches resolved data
          // internally; subsequent renders see the rewrite via
          // rewriteScopeStep and don't need re-hydration.
          //
          // We DO clean up after a tick to allow retry on transient
          // failures — but only if the adapter signaled failure by
          // NOT caching. Today both adapter modules cache eagerly
          // and de-dup themselves, so a sticky inflight entry is
          // fine.
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(scopePath), JSON.stringify(range ?? null)]);
}

// ── Init state ──────────────────────────────────────────────────

let initPromise = null;
let initResolved = false;

function ensureInit() {
  if (initPromise) return initPromise;
  // Auto-init: an analytics schema check so the app can render its
  // shell (timeline endpoints, label cache priming) before firing the
  // first dimension queries. Labels batch-fetch on demand through analytics.
  initPromise = httpQuery("init", {}).catch((error) => {
    // The first permission snapshot can invalidate an in-flight schema read.
    if (error?.name === "AuthContextChangedError") return httpQuery("init", {});
    throw error;
  }).then((result) => {
    initResolved = true;
    return result;
  });
  return initPromise;
}

// ── RPC ─────────────────────────────────────────────────────────

export function query(method, params, { cache = false, staleMs = 0 } = {}) {
  const authContextEpoch = getAuthContextEpoch();
  const key = queryCacheKey(method, params, authContextEpoch);
  // Result-cache read: mirrors useQuery's SWR semantics so callers
  // outside React (manual effects in ScopeColumns, ad-hoc requests in
  // worker code) can hit the same warm cache the hook-based call sites
  // populate. Without this, dragging into the column view re-fetches
  // sparklines the sidebar just finished loading because the cache
  // write happens via useQuery but the read path was hook-only.
  if (staleMs > 0) {
    const cached = queryCache.get(key);
    if (cached && Date.now() - cached.time <= staleMs) {
      return Promise.resolve(cached.result);
    }
  }
  const inflight = inflightQueries.get(key);
  if (inflight) return inflight;

  let promise;
  if (isHttpMethod(method)) {
    promise = httpQuery(method, params);
  } else {
    promise = Promise.reject(new Error(`Unknown query method: ${method}`));
  }

  promise = promise.then((result) => {
    if (getAuthContextEpoch() !== authContextEpoch) {
      const error = new Error("dashboard auth context changed during request");
      error.name = "AuthContextChangedError";
      throw error;
    }
    return result;
  });

  inflightQueries.set(key, promise);
  promise.then(
    (result) => {
      if (cache) rememberQueryResult(key, result);
    },
    () => {},
  ).finally(() => {
    if (inflightQueries.get(key) === promise) inflightQueries.delete(key);
  });
  return promise;
}

export function waitForInit() {
  return ensureInit();
}

export function isReady() {
  return initResolved;
}

// ── React hooks ─────────────────────────────────────────────────

/**
 * Hook that starts the initial analytics schema check and reports a
 * failure through `onError`. Returns true once it resolves.
 */
export function useWorkerReady(onError) {
  const [ready, setReady] = useState(initResolved);
  useEffect(() => {
    let live = true;
    if (initResolved) {
      setReady(true);
      return;
    }
    waitForInit().then(
      () => { if (live) setReady(true); },
      (error) => { if (live) onError?.(error); },
    );
    return () => { live = false; };
  }, [onError]);
  return ready;
}

/**
 * Declarative data-fetching hook with stale-while-revalidate semantics.
 *
 * Returns the previous `data` while a new request is in flight, so
 * components can keep showing content during scope transitions.
 *
 * @param {string} method — worker RPC method name
 * @param {object} params — parameters to pass to the worker
 * @param {{ enabled?: boolean }} options
 * @returns {{ data: T|null, loading: boolean, error: Error|null }}
 */
const inflightQueries = new Map();
const queryCache = new Map();
const MAX_QUERY_CACHE_ENTRIES = 200;

export function _resetQueryCachesForTests() {
  initPromise = null;
  initResolved = false;
  inflightQueries.clear();
  queryCache.clear();
}

function queryCacheKey(method, params, authContextEpoch = getAuthContextEpoch()) {
  return `${authContextEpoch}:${getGenerationRevision()}:${method}:${JSON.stringify(params ?? {})}`;
}

function rememberQueryResult(key, result) {
  queryCache.set(key, { result, time: Date.now() });
  while (queryCache.size > MAX_QUERY_CACHE_ENTRIES) {
    const oldest = queryCache.keys().next().value;
    queryCache.delete(oldest);
  }
}

// ── Scope-hydration epoch ───────────────────────────────────────────
//
// Adapter scope rewrites (identity → its endpoints, etc.) resolve
// asynchronously via hydrateScopeStep. A scope-dependent query fired in
// the same tick as the selection resolves toApiScope() against the
// not-yet-hydrated cache — the adapter drops the step, the query runs
// unscoped, and its result is cached under the *raw* scope-path key. When
// hydration later completes the key is unchanged, so without a nudge the
// stale unscoped result sticks.
//
// So on every hydration completion we (a) clear the result cache so the
// stale rows can't satisfy a refetch, and (b) bump an epoch that
// useQuery / usePagedDimEntries fold into their effect deps — forcing a
// re-run that recomputes toApiScope against the now-hydrated cache.
// Hydration is rare (only when an adapter-derived scope step is selected),
// so the blunt full-cache clear is cheap in practice.
//
// The cluster generation pin uses the SAME mechanism: a pin change is
// another reason every cached result is stale. The refresh revision in the
// query key also separates in-flight work across unpinned refreshes. The
// generationPin module's own subscribers reset the clusterTree and
// cluster labels; here we clear the result cache and bump the epoch so
// live views refetch through the new generation — the in-place
// alternative to a full reload.
let dataEpoch = 0;
const dataEpochSubscribers = new Set();

function bumpDataEpoch() {
  dataEpoch += 1;
  queryCache.clear();
  for (const cb of dataEpochSubscribers) cb();
}

onScopeHydration(bumpDataEpoch);
onGenerationPinChange(bumpDataEpoch);

// The signals facet + count + badges read the catalog's sync accessors through
// `useQuery` (not the reactive `useSignalCatalog` hook), so a facet queried
// before `ListSignals` resolves would cache an empty listing for the session.
// Bump the epoch when the tenant's detection/enabled set actually CHANGES (in
// practice the empty→populated first hydrate) so those queries re-run — the same
// invalidation scope-hydration and the generation pin already trigger. The
// signature guard makes it a no-op when the catalog resolves to (or stays) empty
// and on identical re-emits, so it never triggers a redundant full refetch.
// Deferred dynamic import keeps dataClient importable in test envs that stub the
// API layer, mirroring dimensionDefs' catalog subscription.
import("./signalCatalog.js")
  .then(({ subscribeSignalCatalog, detectionNamespaces, alertCommands }) => {
    let signalSig = null;
    subscribeSignalCatalog(() => {
      const sig = `${detectionNamespaces().slice().sort().join(",")}|${alertCommands().join(",")}`;
      if (sig === signalSig) return;
      const firstObservation = signalSig === null;
      signalSig = sig;
      // Don't bump on the first, still-empty observation (pre-hydration); bump
      // once the listing gains content or changes thereafter.
      if (!firstObservation || sig !== "|") bumpDataEpoch();
    });
  })
  .catch(() => {});

// Test-only seam: read the current epoch so a test can assert whether a bump
// fired (the signals-catalog subscription above has no other observable effect).
export function _dataEpochForTests() { return dataEpoch; }

export function useDataEpoch() {
  const [, bump] = useReducer((x) => x + 1, 0);
  useEffect(() => {
    dataEpochSubscribers.add(bump);
    return () => { dataEpochSubscribers.delete(bump); };
  }, []);
  return dataEpoch;
}

function useAuthContextEpoch() {
  return useSyncExternalStore(
    onAuthContextChange,
    getAuthContextEpoch,
    getAuthContextEpoch,
  );
}

// Default cache TTL. The dashboard is read-only against analytics, so a
// 30-second stale-while-revalidate window is safe and collapses the
// sequential identical fetches that components issue at slightly
// different points in the render commit pass (e.g. five components
// each asking "total prompts in this scope" within ~50ms of a scope
// change). Callers that need strict freshness override per call (the
// EVENT execution-log fetch passes 0).
const DEFAULT_STALE_MS = 30_000;

export function useQuery(method, params, { enabled: enabledOpt = true, staleMs = DEFAULT_STALE_MS } = {}) {
  // Guard before firing: if the caller is resolved to lack the permission this
  // method's backend RPC requires, the request can only 403, so skip it rather
  // than spam the dev-tools console with denials.
  // `hasPermission` fails open while caps are loading / degraded / pre-RBAC, so
  // this only suppresses queries for a role we KNOW lacks the permission.
  const caps = useMyPermissions();
  const permitted = queryPermitted(method, caps);
  const enabled = enabledOpt && permitted;
  // `denied` is true only when the caller WANTED this query but the role is
  // KNOWN to lack its permission. queryPermitted fails open while caps load, so
  // this never trips mid-load — it lets a caller show a denial hint instead of a
  // perpetual "loading" for a disabled query that will never resolve.
  const denied = enabledOpt && !permitted;

  // Hold scope-dependent queries while an adapter scope step (e.g. an
  // identity → its endpoints) is still hydrating. Firing now would resolve
  // toApiScope against the cold cache, drop the step, and run UNSCOPED —
  // the whole-tenant flash that then snaps down once the rewrite lands. We
  // recompute on every render (cheap) and re-run the fetch when the scope
  // epoch bumps on hydration completion (awaitingScope flips false).
  const awaitingScope = scopePathAwaitingHydration(
    params?.scopePath ?? params?.parentScopePath ?? null,
    params?.range ?? null,
  );
  const authContextEpoch = useAuthContextEpoch();
  // The params the state's data was read for (`paramsKey` in state): while
  // new params load, the data is still the last ones', which the hook says
  // (`previousParams`) for a caller that must not take it for the new.
  const paramsKey = JSON.stringify(params);
  // Seed initial state from the cache on first mount so a remount with the
  // same params (e.g. hover-out closing a transient panel) doesn't flash
  // a "loading" state before the effect re-runs. Without this, even cached
  // hits paint one Computing… frame on remount because useState's initial
  // value isn't aware of the cache.
  const [state, setState] = useState(() => {
    if (!enabled || awaitingScope || staleMs <= 0) {
      return { data: undefined, loading: enabled, error: null, authContextEpoch, paramsKey };
    }
    const cached = queryCache.get(queryCacheKey(method, params));
    if (!cached) return { data: undefined, loading: true, error: null, authContextEpoch, paramsKey };
    const fresh = Date.now() - cached.time <= staleMs;
    return { data: cached.result, loading: !fresh, error: null, authContextEpoch, paramsKey };
  });
  const requestRef = useRef(0);
  // Unmounted, it hears nothing more: a request still out sets no state when
  // it settles (after a test's page is torn down, a state update throws).
  useEffect(() => () => { requestRef.current += 1; }, []);
  // Re-run when an adapter scope-step finishes hydrating, or when the
  // cluster generation pin changes, so the query refetches against the
  // resolved scope / pinned generation (see useDataEpoch).
  const epoch = useDataEpoch();

  useEffect(() => {
    if (!enabled) {
      requestRef.current += 1;
      setState((prev) => (prev.loading && prev.authContextEpoch === authContextEpoch
        ? { ...prev, loading: false }
        : { data: undefined, loading: false, error: null, authContextEpoch, paramsKey }));
      return;
    }
    if (awaitingScope) {
      // Scope step still hydrating — cancel any in-flight request and stay
      // loading. The epoch bump on completion re-runs this effect with a
      // warm cache (awaitingScope → false), and the fetch goes out scoped.
      requestRef.current += 1;
      setState((prev) => (prev.loading && prev.authContextEpoch === authContextEpoch
        ? prev
        : { data: undefined, loading: true, error: null, authContextEpoch, paramsKey }));
      return;
    }

    const reqId = ++requestRef.current;
    const shouldCache = staleMs > 0;
    const cacheKey = queryCacheKey(method, params);
    const cached = shouldCache ? queryCache.get(cacheKey) : null;
    if (cached) {
      const fresh = staleMs > 0 && Date.now() - cached.time <= staleMs;
      setState({ data: cached.result, loading: !fresh, error: null, authContextEpoch, paramsKey });
      if (fresh) return;
    }

    setState((prev) => prev.authContextEpoch === authContextEpoch
      ? { ...prev, loading: true }
      : { data: undefined, loading: true, error: null, authContextEpoch, paramsKey });

    query(method, params, { cache: shouldCache }).then(
      (result) => {
        if (reqId === requestRef.current) {
          setState({ data: result, loading: false, error: null, authContextEpoch, paramsKey });
        }
      },
      (err) => {
        if (reqId === requestRef.current) {
          setState((prev) => ({ ...prev, loading: false, error: err, authContextEpoch }));
        }
      },
    );
  }, [method, paramsKey, enabled, awaitingScope, epoch, authContextEpoch]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fail closed in the render triggered by an auth change, before effects have
  // had a chance to start the replacement request. This avoids one frame of
  // the previous tenant's stale-while-revalidate data.
  const { paramsKey: dataParamsKey, ...visibleState } = state.authContextEpoch === authContextEpoch
    ? state
    : { data: undefined, loading: enabled, error: null, authContextEpoch, paramsKey };
  return { ...visibleState, denied, previousParams: dataParamsKey !== paramsKey };
}

/**
 * Paginated Explore Inventory rows, or with `method` "getFleetEndpoints"
 * Fleet's endpoints with their sessions. Page zero replaces the current result
 * set; later pages append in server order. Scope/search/sort changes
 * invalidate any in-flight page and start again at page zero.
 */
export function usePagedInventoryRows({
  method = "getInventoryRows", scopePath = [], range, search = "", sortBy = null, endpointIds = null, pageSize = 1000, enabled: enabledOpt = true,
} = {}) {
  const authContextEpoch = useAuthContextEpoch();
  const caps = useMyPermissions();
  const enabled = enabledOpt && queryPermitted(method, caps);
  const denied = enabledOpt && !enabled;
  const [pages, setPages] = useState([]);
  const [total, setTotal] = useState(null);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState(null);
  const [loadedParamsKey, setLoadedParamsKey] = useState(null);
  const [resultAuthContextEpoch, setResultAuthContextEpoch] = useState(authContextEpoch);
  const reqRef = useRef(0);
  const loadMoreRequestRef = useRef(null);
  const epoch = useDataEpoch();
  const awaitingScope = scopePathAwaitingHydration(scopePath, range);
  const paramsKey = JSON.stringify({
    method, scopePath, range, search, sortBy, endpointIds, pageSize, epoch, authContextEpoch,
  });

  useEffect(() => {
    if (!enabled) {
      ++reqRef.current;
      loadMoreRequestRef.current = null;
      setPages([]);
      setTotal(null);
      setLoadedParamsKey(null);
      setError(null);
      setLoading(false);
      return;
    }
    if (awaitingScope) {
      ++reqRef.current;
      loadMoreRequestRef.current = null;
      setLoading(true);
      return;
    }

    const reqId = ++reqRef.current;
    loadMoreRequestRef.current = null;
    setLoading(true);
    setError(null);
    query(method, { scopePath, range, search, sortBy, endpointIds, page: 0, pageSize }).then(
      (res) => {
        if (reqId !== reqRef.current) return;
        const firstPage = res?.rows ?? [];
        setPages([firstPage]);
        setTotal(res?.total ?? firstPage.length);
        setLoadedParamsKey(paramsKey);
        setResultAuthContextEpoch(authContextEpoch);
        setLoading(false);
      },
      (err) => {
        if (reqId !== reqRef.current) return;
        setError(err);
        setResultAuthContextEpoch(authContextEpoch);
        setLoading(false);
      },
    );
  }, [paramsKey, enabled, awaitingScope, pageSize]); // eslint-disable-line react-hooks/exhaustive-deps

  const accumulatedRows = useMemo(() => pages.flatMap((page) => page), [pages]);
  const current = enabled && !awaitingScope && loadedParamsKey === paramsKey;
  const rows = current ? accumulatedRows : [];
  const loadedCount = rows.length;
  const currentTotal = current ? total : null;
  const hasMore = currentTotal != null && loadedCount < currentTotal;
  const currentAuthContext = resultAuthContextEpoch === authContextEpoch;
  const visibleLoading = currentAuthContext ? loading : enabled;
  const visibleError = currentAuthContext ? error : null;
  const loadMore = useCallback(() => {
    if (loading || !hasMore || loadMoreRequestRef.current) return;
    const reqId = reqRef.current;
    const page = pages.length;
    const request = { reqId, page };
    loadMoreRequestRef.current = request;
    setLoading(true);
    setError(null);
    query(method, { scopePath, range, search, sortBy, endpointIds, page, pageSize }).then(
      (res) => {
        if (reqId !== reqRef.current) return;
        setPages((prev) => [...prev, res?.rows ?? []]);
        setTotal(res?.total ?? total);
        setLoading(false);
      },
      (err) => {
        if (reqId !== reqRef.current) return;
        setError(err);
        setLoading(false);
      },
    ).finally(() => {
      if (loadMoreRequestRef.current === request) loadMoreRequestRef.current = null;
    });
  }, [loading, hasMore, pages.length, paramsKey, pageSize]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    rows,
    total: currentTotal,
    loadedCount,
    hasMore,
    loadMore,
    loading: visibleLoading,
    error: visibleError,
    denied,
  };
}

/**
 * Paged version of getDimensionEntries: fetches page 0 on params change,
 * exposes loadMore() for subsequent pages, and accumulates results.
 *
 * Page 0 carries the top-N entries by activity. Its `maxValue` is the
 * page-0 (and therefore global) max — we persist it across pages so the
 * sidebar's share bars stay proportional even as smaller-value entries
 * stream in below.
 *
 * Returns:
 *   entries       — accumulated entries across all loaded pages, share
 *                   recomputed against the persistent globalMax.
 *   total         — server's distinct count for the dim+scope+range.
 *   loadedCount   — entries.length (cheaper than re-counting).
 *   hasMore       — true if loadedCount < total.
 *   loadMore()    — fetches the next page; no-op while a fetch is in flight
 *                   or when hasMore is false.
 *   initialLoad   — true while page 0 is still in flight.
 *   revalidating  — true when params change but we still have stale pages.
 */
export function usePagedDimEntries({ dimKey, parentScopePath = [], pageSize = 100, enabled: enabledOpt = true, search = "", sort = "activity", signalRev, steer = "" }) {
  const range = useCurrentRange();
  const authContextEpoch = useAuthContextEpoch();
  // Guard before firing (see useQuery): a role resolved to lack the dimension
  // query's permission would only 403, so don't fetch page 0 at all.
  const caps = useMyPermissions();
  const enabled = enabledOpt && queryPermitted("getDimensionEntries", caps);
  const [pages, setPages] = useState([]);
  const [total, setTotal] = useState(0);
  const [globalMax, setGlobalMax] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [errorAuthContextEpoch, setErrorAuthContextEpoch] = useState(authContextEpoch);
  const [pagesAuthContextEpoch, setPagesAuthContextEpoch] = useState(authContextEpoch);
  const [pagesDimKey, setPagesDimKey] = useState(dimKey);
  const reqRef = useRef(0);
  // Range is part of paramsKey so a date-range change refetches page 0
  // and resets the accumulator. Epoch is folded in so child panes under
  // an adapter-derived scope step (e.g. clusters under a selected
  // identity) refetch once that step's endpoints finish hydrating — and
  // so every pane refetches when the cluster generation pin changes.
  const epoch = useDataEpoch();
  // Hold page-0 while a parent adapter scope step (e.g. clusters/endpoints
  // under a just-selected identity) is still hydrating — otherwise toApiScope
  // drops the step and page 0 loads against the whole tenant before snapping
  // down. Re-runs once the epoch bumps on hydration completion.
  const awaitingScope = scopePathAwaitingHydration(parentScopePath, range);
  // signalRev: a token that flips when a signal scan settles, so signal-scoped
  // facet entries refetch as the fired set lands (undefined when no signal is
  // in scope — JSON.stringify drops it, so non-signal keys are unchanged).
  const paramsKey = JSON.stringify({ dimKey, parentScopePath, search, sort, range, epoch, authContextEpoch, signalRev, steer });

  useEffect(() => {
    if (!enabled) return;
    if (awaitingScope) {
      // Don't fetch against a not-yet-resolved scope step; stay loading so
      // the pane shows progress (initialLoad / revalidating) until the
      // epoch bump re-runs this with the resolved endpoint set. Bump reqRef
      // first so an in-flight page-0 fetch from the PRIOR parentScopePath
      // (e.g. the overview's, before an identity was selected) can't still
      // write setPages/setTotal/setGlobalMax — same cancellation useQuery does.
      ++reqRef.current;
      setLoading(true);
      return;
    }
    const reqId = ++reqRef.current;
    setLoading(true);
    setError(null);
    query("getDimensionEntries", { dimKey, parentScopePath, offset: 0, limit: pageSize, search, sort, range, signalRev, query: steer }).then(
      (res) => {
        if (reqId !== reqRef.current) return;
        setPages([res.entries ?? []]);
        setPagesAuthContextEpoch(authContextEpoch);
        setPagesDimKey(dimKey);
        setTotal(res.total ?? (res.entries?.length ?? 0));
        setGlobalMax(res.maxValue ?? 0);
        setLoading(false);
      },
      (err) => {
        if (reqId !== reqRef.current) return;
        setError(err);
        setErrorAuthContextEpoch(authContextEpoch);
        setLoading(false);
      },
    );
  }, [paramsKey, enabled, awaitingScope, pageSize]); // eslint-disable-line react-hooks/exhaustive-deps

  // Stale pages may stand in while search, sort, range or scope revalidate, but
  // never across an auth context or a dimension: a caller that is reused for a
  // new dimKey renders rows with that dimKey, so a previous dimension's ids
  // would be resolved as the new dimension's (topic names for model ids).
  const pagesCurrent = pagesAuthContextEpoch === authContextEpoch && pagesDimKey === dimKey;

  const loadMore = useCallback(() => {
    if (loading) return;
    const visiblePages = pagesCurrent ? pages : [];
    const offset = visiblePages.reduce((n, p) => n + p.length, 0);
    if (offset === 0 || (total > 0 && offset >= total)) return;
    const reqId = reqRef.current;
    setLoading(true);
    query("getDimensionEntries", { dimKey, parentScopePath, offset, limit: pageSize, search, sort, range, signalRev, query: steer }).then(
      (res) => {
        if (reqId !== reqRef.current) return;
        setPages((prev) => [...prev, res.entries ?? []]);
        setLoading(false);
      },
      () => { if (reqId === reqRef.current) setLoading(false); },
    );
  }, [pages, pagesCurrent, total, loading, paramsKey, pageSize]); // eslint-disable-line react-hooks/exhaustive-deps

  const entries = useMemo(() => {
    if (!pagesCurrent) return [];
    const flat = pages.flatMap((p) => p);
    if (!globalMax || globalMax <= 0) return flat;
    // Page-0 entries already have share against page-0's local max (which
    // equals globalMax), so they're correct. Subsequent pages need their
    // share rescaled. Doing it once here keeps the row template ignorant.
    return flat.map((e) => {
      const rawShare = Math.round((e.activity / globalMax) * 100);
      const share = e.activity > 0 ? Math.max(1, rawShare) : 0;
      return e.share === share ? e : { ...e, share };
    });
  }, [pages, globalMax, pagesCurrent]);

  const loadedCount = entries.length;
  const visibleTotal = pagesCurrent ? total : 0;
  const hasMore = visibleTotal > 0 && loadedCount < visibleTotal;
  // Treat an auth-context or dimension transition as an initial load
  // immediately, before the effect above has a chance to update the previous
  // request's state. This prevents a one-render empty-state flash while the
  // new first page is still pending.
  const visibleLoading = pagesCurrent ? loading : enabled;
  const initialLoad = visibleLoading && entries.length === 0;
  const revalidating = visibleLoading && entries.length > 0;

  const visibleError = errorAuthContextEpoch === authContextEpoch ? error : null;
  return { entries, total: visibleTotal, loadedCount, hasMore, loadMore, loading: visibleLoading, error: visibleError, initialLoad, revalidating };
}
